import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Zap, Bot } from "lucide-react";

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background" aria-label="Hero section">
      {/* Simplified background - fewer DOM nodes, uses CSS instead of extra divs */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(hsl(160, 100%, 50%) 1px, transparent 1px), linear-gradient(90deg, hsl(160, 100%, 50%) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-5xl mx-auto"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full neon-border bg-primary/5 mb-8">
            <Sparkles className="w-4 h-4 text-primary" aria-hidden="true" />
            <span className="text-sm font-medium text-primary">Digital Marketing Academy | AI & Automation Coaching in Gwalior</span>
          </div>

          {/* Main Heading - critical for LCP */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight">
            Best <span className="text-gradient">Digital Marketing</span>
            <br />
            <span className="text-gradient-accent">Academy</span> in Gwalior
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed">
            Gwalior's No.1 Digital Marketing & AI Automation Coaching by <strong className="text-foreground">Sumit Sharma</strong> — Master Digital Marketing, AI Tools & Automation with practical training, live projects & job placement support
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {["Live + Recorded", "Online & Offline", "Job Placement", "Freelancing Ready", "Certificate"].map((tag) => (
              <span key={tag} className="px-4 py-2 rounded-full text-sm border border-border bg-card/50 text-muted-foreground">
                {tag}
              </span>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 rounded-full shadow-elegant hover:glow-green transition-all duration-300 font-semibold"
                aria-label="Enroll now for Digital Marketing and AI course"
              >
                <Zap className="mr-2 w-5 h-5" aria-hidden="true" />
                Enroll Now
              </Button>
            </Link>
            <Link to="/free-workshop">
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 rounded-full border-primary/30 hover:bg-primary/10 hover:border-primary/50"
                aria-label="Join our free AI workshop"
              >
                <Bot className="mr-2 w-5 h-5" aria-hidden="true" />
                Free Workshop
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10" aria-hidden="true">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-6 h-10 border-2 border-primary/30 rounded-full flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-1.5 bg-primary/50 rounded-full" />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
